import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useGraphEditorStore } from '@/graph-store';
import type { ConversionPlan } from '@/graph-store/actions/archetypeConversion';

const ARCHETYPE_LABEL: Record<string, string> = {
  TRANSFORM: 'Transform',
  MEASURE: 'Measure',
  TRANSFER: 'Transfer',
  COMPLEX: 'Complex',
};

export interface ArchetypeChangeDialogProps {
  plan: ConversionPlan;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Shown before an archetype change that destroys or disconnects materials, so
 * the user sees the full downstream cascade rather than discovering it after.
 */
export function ArchetypeChangeDialog({
  plan,
  onConfirm,
  onCancel,
}: ArchetypeChangeDialogProps) {
  const materialNodes = useGraphEditorStore((s) => s.graphData.materialNodes);
  const operationNodes = useGraphEditorStore((s) => s.graphData.operationNodes);

  const nameOfMaterial = (id: string) => materialNodes[id]?.name ?? id;
  const nameOfOperation = (id: string) => operationNodes[id]?.displayName ?? id;

  const blocked = plan.blockers.length > 0;
  const from = ARCHETYPE_LABEL[plan.from] ?? plan.from;
  const to = ARCHETYPE_LABEL[plan.to] ?? plan.to;

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        Change step from {from} to {to}?
      </DialogTitle>
      <DialogContent dividers>
        {blocked ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {plan.blockers.map((blocker) => (
              <div key={blocker}>{blocker}</div>
            ))}
          </Alert>
        ) : (
          <DialogContentText sx={{ mb: 2 }}>
            A {to.toLowerCase()} step has a different shape, so the graph around
            this step has to be rewired. You can undo this in one step.
          </DialogContentText>
        )}

        {plan.materialsToDelete.length > 0 && (
          <Section
            title="Will be deleted"
            severity="warning"
            items={plan.materialsToDelete.map(nameOfMaterial)}
          />
        )}

        {plan.operationsToDelete.length > 0 && (
          <Section
            title="Steps removed with them"
            severity="warning"
            items={plan.operationsToDelete.map(nameOfOperation)}
          />
        )}

        {plan.materialsToDetach.length > 0 && (
          <Section
            title="Will be disconnected but kept"
            severity="info"
            items={plan.materialsToDetach.map(nameOfMaterial)}
          />
        )}

        {plan.clearFields.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 2 }}>
            {from}-specific settings on this step will be cleared.
          </Typography>
        )}

        {plan.warnings.map((warning) => (
          <Alert key={warning} severity="info" sx={{ mt: 1 }}>
            {warning}
          </Alert>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          color={plan.isLossy ? 'error' : 'primary'}
          disabled={blocked}
          onClick={onConfirm}
        >
          {plan.isLossy ? 'Change and delete' : 'Change step'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Section({
  title,
  items,
  severity,
}: {
  title: string;
  items: string[];
  severity: 'warning' | 'info';
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="subtitle2"
        color={severity === 'warning' ? 'error.main' : 'text.secondary'}
      >
        {title}
      </Typography>
      <List dense disablePadding>
        {items.map((item, index) => (
          <ListItem key={`${item}-${index}`} disablePadding sx={{ pl: 1 }}>
            <ListItemText primary={`• ${item}`} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
